/**
 * <uhs-cart-limit> — brings a cart line back down to its per-order maximum.
 *
 * Used for products tagged UHS-Daily-Limit-<n> (at most n per order; rule in
 * snippets/uhs-order-limit.liquid). The cart
 * drawer and cart page render this element ONLY on a line whose quantity is
 * above its maximum (snippets/cart-drawer.liquid, sections/main-cart-items.liquid),
 * so it has nothing to decide: it asks the surrounding Dawn cart element to set
 * that line to the maximum, through the same updateQuantity() a quantity change
 * uses. That re-renders the cart sections, publishes the cart update and fires
 * the Standard Events cart-lines event exactly as a shopper edit would.
 *
 * Why after the fact rather than blocking each Add to Cart button: a course can
 * be added from the product page, product cards, quick add, the sister-SKU
 * offer, the bulk order form or an app via Shopify.actions. Every one of those
 * ends with the cart drawer re-rendering, so correcting here covers all of them
 * with one piece of code. It is NOT a hard guarantee - a checkout reached
 * without the cart ever rendering (e.g. a direct /cart/<id>:2 link) is not
 * caught. Hard enforcement needs a Shopify cart and checkout validation
 * Function, which lives outside this theme.
 *
 * One line at a time: when several lines are over the limit, only the first
 * acts; the re-render that follows brings the next one back.
 *
 * Each ELEMENT acts once, not each line: the same course can go over the limit
 * again later in the visit (a second add), and that render brings a fresh
 * element that must act too. A failed request cannot loop - on an error Dawn's
 * updateQuantity() returns before re-rendering the cart, so no new element
 * appears until the shopper does something.
 */
if (!customElements.get('uhs-cart-limit')) {
  customElements.define(
    'uhs-cart-limit',
    class UhsCartLimit extends HTMLElement {
      connectedCallback() {
        const cartItems = this.closest('cart-drawer-items, cart-items');
        if (!cartItems) return;

        const tagName = cartItems.tagName.toLowerCase();
        customElements.whenDefined(tagName).then(() => this.whenVisible(cartItems));
      }

      disconnectedCallback() {
        this.drawerObserver?.disconnect();
      }

      /**
       * Inside the drawer, wait until it is open. updateQuantity() ends by
       * trapping focus in the drawer, which on a closed drawer would strand
       * keyboard focus in hidden content. After an add, Dawn opens the drawer
       * right after re-rendering it, so in practice this waits a frame.
       * @param {HTMLElement} cartItems
       */
      whenVisible(cartItems) {
        const drawer = this.closest('cart-drawer');
        if (!drawer || drawer.classList.contains('active')) {
          this.correct(cartItems);
          return;
        }

        this.drawerObserver = new MutationObserver(() => {
          if (!drawer.classList.contains('active')) return;
          this.drawerObserver.disconnect();
          this.correct(cartItems);
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
      }

      correct(cartItems) {
        if (!this.isConnected) return;
        if (cartItems.querySelector('uhs-cart-limit') !== this) return;

        if (this.corrected) return;
        this.corrected = true;

        const line = parseInt(this.dataset.line, 10);
        const max = parseInt(this.dataset.max, 10);
        if (!line || Number.isNaN(max) || typeof cartItems.updateQuantity !== 'function') return;

        // updateQuantity reads event.currentTarget to label the update; this
        // element stands in for the quantity input a shopper would have used.
        cartItems.updateQuantity(line, max, { currentTarget: this }, null, this.dataset.variantId);
      }
    },
  );
}
